import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { useUserNotificationSubscription } from '@ps/web-shared-generated-graphql/notifications';
import type { Notification } from '@ps/web-shared-generated-graphql/notifications';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { NotificationServiceTopic } from '@swf/core/enums';
import useRequest from '@swf/core/hooks/useRequest';
import { geoComplyPLC } from '@swf/core/models/notification/geoComplyPLC/geoComplyPLC';
import { select } from '@swf/core/store';
import type { GeoComplyPLC } from '@swf/core/types/Notification';
import { handleNotifications } from '@swf/core/utils/notifications';

const useNotificationSubscription = (client: ApolloClient<NormalizedCacheObject>) => {
  const dispatch = useDispatch();
  const request = useRequest();
  const store = useStore();
  const state = store.getState();
  const isExternalPartnerSite = useSelector(select.cms.isExternalPartnerSite);
  const { idpToken = '' } = useSelector(select.session.get);

  let topics = [NotificationServiceTopic.PAM_SG];
  if (isExternalPartnerSite)
    topics = [
      ...topics,
      NotificationServiceTopic.PAM_AUTH,
      NotificationServiceTopic.RCS,
      NotificationServiceTopic.PSN_PARTNER
    ];

  const parseNotification = async (notification: Notification) => {
    try {
      const { type, payload } = notification;
      const body = payload ? JSON.parse(decodeURI(payload)) : {};

      const currentModal = select.modal.getCurrent(state)?.path as string;
      const isSiteUS = select.cms.isSiteUS(state);
      const license = select.account.getLicense(state);
      const locale = select.locale.get(state);
      const features = select.feature.get(state);
      const site = select.cms.getSiteId(state);
      const isSiteCAON = site === Sites.CA_ON;

      const handleGeoComply = (props: GeoComplyPLC) =>
        geoComplyPLC({
          ...props,
          isSiteUS,
          isSiteCAON,
          enqueue: dispatch.modal.enqueue,
          license,
          request: (props) => request({ ...select.cms.getRequestOptions(state), ...props })
        });

      await handleNotifications({
        body,
        currentModal,
        features,
        geoComplyPLC: handleGeoComply,
        isSiteUS,
        license,
        locale,
        logout: dispatch.auth.logout,
        modal: dispatch.modal,
        site,
        type,
        isSdk: false
      });
    } catch {
      // proceed to next message in case message is faulty
    }
  };

  useUserNotificationSubscription({
    shouldResubscribe: () => !idpToken,
    fetchPolicy: 'no-cache',
    onData: (res) => {
      const data = res.data.data?.subscribe;
      if (data) {
        if ('payload' in data) {
          parseNotification(data);
        } else {
          data.notifications?.forEach((n) => {
            if (n) parseNotification(n);
          });
        }
      }
    },
    client,
    variables: { topics }
  });
};

export default useNotificationSubscription;
